import { type MoneyString, isMoneyString, toMinorUnits } from "@ibada/shared-types/finance"

import {
  currencyFormatOptions,
  DEFAULT_CURRENCY_LOCALE,
  formatCurrency,
  type DisplayAmount,
} from "@/shared/lib/formatting/currency"

/** Coerce anything non-finite (NaN, Infinity, null) to 0 so charts/labels never render "NaN". */
function safe(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

/**
 * Guards a money amount without CONVERTING it.
 *
 * A canonical decimal string (IBA-116) is passed through untouched — `Intl` parses it exactly, and running it
 * through `Number()` first is precisely how a cent used to disappear from a large amount. A `number` is still
 * accepted, because chart geometry legitimately produces one.
 */
function safeAmount(value: DisplayAmount | null | undefined): DisplayAmount {
  if (typeof value === "string") {
    const trimmed = value.trim()
    return isMoneyString(trimmed) ? trimmed : 0
  }
  return safe(value)
}

/**
 * The "currency" a ratio response carries instead of a currency code (ADR-0024).
 *
 * When branch autonomy withholds the absolutes, the server sends every money figure as a SHARE of its
 * group's total and flags it on `meta.figuresAreRatios`. The overview query then replaces `meta.currency`
 * with this sentinel, so the twenty-odd widgets that already read `meta.currency` render percentages without
 * one of them having to learn what governance is. A sentinel rather than a second parameter threaded through
 * every widget: the alternative was twenty-one call sites that each had to remember, and the ones that forgot
 * would print a share with a dollar sign in front of it.
 */
export const RATIO_DISPLAY_CURRENCY = "__ratio__"

/** Whether a figure formatted under this currency is a share of a total rather than an amount. */
export const isRatioDisplay = (currency: string | null | undefined): boolean =>
  currency === RATIO_DISPLAY_CURRENCY

/** Full currency for tooltips / headline amounts, hardened against invalid amounts. Never hardcodes a code. */
export function formatMoneySafe(amount: DisplayAmount | null | undefined, currency: string): string {
  if (isRatioDisplay(currency)) return formatShare(safeAmount(amount))
  return formatCurrency(safeAmount(amount), currency)
}

/**
 * A share, to one decimal — the scale the server rounded to. Never rounded further here: a breakdown whose
 * slices no longer sum to 100% reads as a bug in the books rather than in the formatter.
 */
export function formatShare(value: DisplayAmount | null | undefined): string {
  const numeric = typeof value === "string" ? Number.parseFloat(value) : (value ?? 0)
  const safeValue = Number.isFinite(numeric) ? (numeric as number) : 0
  return `${Number.isInteger(safeValue) ? safeValue : safeValue.toFixed(1)}%`
}

/** Always-compact currency for axis ticks and tight rows: `$1.2K`, `$3.4M`. */
export function formatCompactCurrency(
  amount: DisplayAmount | null | undefined,
  currency: string,
  locale: string = DEFAULT_CURRENCY_LOCALE,
): string {
  // A share is already compact, and compacting a percentage ("1.2K%") is nonsense.
  if (isRatioDisplay(currency)) return formatShare(safeAmount(amount))
  return new Intl.NumberFormat(locale, {
    ...currencyFormatOptions(currency),
    currency,
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
    // `format` accepts a decimal string and parses it exactly; the cast is only for TS's lib types, which did
    // not learn about string input until ES2023.
  }).format(safeAmount(amount) as number)
}

/** Always-compact plain number: `1.2K`, `3.4M`. */
export function formatCompactNumber(value: number | null | undefined, locale: string = DEFAULT_CURRENCY_LOCALE): string {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(safe(value))
}

/** Whole-number percentage, clamped/guarded. Returns e.g. `73%`. */
export function formatPercent(value: number | null | undefined): string {
  return `${Math.round(safe(value))}%`
}

/** utilization ratio (0–100) from actual vs planned, guarding divide-by-zero. NOT for money — see below. */
export function utilizationPercent(actual: number | null | undefined, planned: number | null | undefined): number {
  const p = safe(planned)
  if (p <= 0) return 0
  return (safe(actual) / p) * 100
}

/** One percent, in basis points. Scaling before an integer division is what keeps a fractional percent alive. */
const BASIS_POINTS_IN_A_WHOLE = 10_000n
const BASIS_POINTS_PER_PERCENT = 100

/**
 * `actual` as a percentage of `planned`, for MONEY — exactly (IBA-116).
 *
 * `utilizationPercent` above divides two floats. That is fine for a count of days; it is not fine for two amounts
 * that have each already lost precision on the way in, and "have we spent 100% of the budget?" is exactly the
 * question you do not want answered by a value that drifted.
 *
 * Here the division happens in integer minor units and only the UNITLESS RESULT becomes a number — a percentage
 * has no currency and no minor unit, and nobody is owed money in it. A plan of zero (or less) has no percentage:
 * nothing can be a share of nothing, so it reports 0 rather than Infinity.
 */
export function moneyUtilizationPercent(actual: MoneyString, planned: MoneyString, currency: string): number {
  const plannedMinor = toMinorUnits(planned, currency)
  if (plannedMinor <= 0n) return 0
  const actualMinor = toMinorUnits(actual, currency)
  return Number((actualMinor * BASIS_POINTS_IN_A_WHOLE) / plannedMinor) / BASIS_POINTS_PER_PERCENT
}

/**
 * Whether `moneyUtilizationPercent` against this plan means anything.
 *
 * It returns 0 both for "a plan of zero or less" and for "nothing achieved yet against a real plan", and a
 * caller cannot tell those apart from the result. Asking the plan directly can: a budget planned to break
 * even or run a deficit has no percentage to report, and should say something else entirely rather than
 * render a 0% that reads as failure.
 */
export function hasMeaningfulPlan(planned: MoneyString, currency: string): boolean {
  return toMinorUnits(planned, currency) > 0n
}
